import { observer } from 'mobx-react';
import { FC, useCallback } from 'react';
import { franchiseManagementStore } from '../../store/franchiseManagementStore';
import { franchiseBranchCast } from '../../types/franchiseManagement';
import { localeStore } from '~/mobx/localesStore';
import { modalStore } from '~/mobx/modalStore';
import askAgain, { AskAgainKey } from '~/services/askAgain';
import { BRANCHES_MODAL_FETCH_PARAMETER, IBranch, UNAVAILABLE_BRANCHES_FLAG } from '~/types/branches';
import { getTranslate } from '~/utils/helpers';
import { IAskAgainModalSubmit } from '~/view/containers/Modals/AskAgainModal/AskAgainModal';
import { BranchesModalProfile, IBranchesModalItem } from '~/view/containers/Modals/BranchesModal/BranchesModal';
import ProfileBranches from '~/view/containers/ProfileBranches/ProfileBranches';
import { LoadingStatus } from '~/types/main';
import branchesModalStore from '~/view/containers/Modals/BranchesModal/store/branchesModalStore';
import { appStore } from '~/mobx/appStore';

export const FranchiseProfileBranches: FC = observer((): JSX.Element => {
  const handleBranchesEdit = useCallback((branches: IBranchesModalItem[]) => {
    franchiseManagementStore.setProfileBranches(branches.map(franchiseBranchCast));
    modalStore.closeBranchesModal();
  }, []);

  const checkFreezeBranch = useCallback(
    (branch: IBranch): boolean => {
      if (franchiseManagementStore.isSelectedDefaultProfile) {
        return branch?.franchise_profile === franchiseManagementStore.selectedFranchiseProfile.name;
      }
      return false;
    },
    [franchiseManagementStore.selectedFranchiseProfile.id]
  );

  const checkUnavailableBranch = useCallback(
    (branch: IBranch): boolean => {
      console.info(appStore.settings);
      if (!branchesModalStore.selectedBranches.length || !appStore.settings?.is_pos) return false;
      return branchesModalStore.selectedBranches[0].pos_service_id ? branch?.pos_service_id === '' : branch?.pos_service_id !== '';
    },
    [franchiseManagementStore.selectedFranchiseProfile.id]
  );

  const askAgainChecker = useCallback(
    (branch: IBranch | undefined): boolean => {
      if (!branch || !branch.franchise_profile) return false;
      return branch.franchise_profile !== franchiseManagementStore.selectedFranchiseProfile.name;
    },
    [franchiseManagementStore.selectedFranchiseProfile.name]
  );

  const askAgainHandler = (branch: IBranch | undefined, callback: (branch: IBranch, data: IAskAgainModalSubmit) => void) => {
    if (!branch || !branch.franchise_profile) return true;

    askAgain.showModal(
      {
        submit: (data) => askAgainSubmit(data, branch, callback),
        message: `${localeStore.t('are_you_sure_want_to_change_profile')} ${getTranslate(branch.name)} ${localeStore.t('txt_from')} ${
          branch.franchise_profile
        } ${localeStore.t('txt_select_to')} ${franchiseManagementStore.selectedFranchiseProfile.name} ?`,
      },
      AskAgainKey.FRANCHISE_PROFILE
    );
  };

  const askAgainSubmit = (data: IAskAgainModalSubmit, branch: IBranch, callback: (branch: IBranch, data: IAskAgainModalSubmit) => void) => {
    askAgain.hideModal(AskAgainKey.FRANCHISE_PROFILE, data.checkBoxStatus);
    callback(branch, data);
  };

  const openBranchesModal = () => {
    modalStore.openBranchesModal({
      selectedBranches: franchiseManagementStore.selectedFranchiseProfile.branches,
      onSave: handleBranchesEdit,
      // shouldCheckPosConflict: !franchiseManagementStore.isSelectedDefaultProfile,
      isFreezeOption: franchiseManagementStore.isSelectedDefaultProfile ? checkFreezeBranch : checkUnavailableBranch,
      askAgainChecker,
      askAgainHandler,
      headerTitle: localeStore.t('adding_branches_to_profile_for_fast'),
      isMulti: true,
      saveWithoutBranches: true,
      hideControls: true,
      fetchParamStatus: BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE,
      fetchParamDelete: BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE,
      checkUnavailable: true,
      unavailableFlag: UNAVAILABLE_BRANCHES_FLAG.ALL,
      highlightUnavailable: true,
      profileNamesToShow: [BranchesModalProfile.FRANCHISE],
    });
  };

  return (
    <ProfileBranches
      selectedProfile={franchiseManagementStore.selectedFranchiseProfile}
      onDeleteProfile={franchiseManagementStore.deleteProfile}
      onDeleteBranch={franchiseManagementStore.removeBranchFromProfile}
      onBranchesModalOpen={openBranchesModal}
      loading={franchiseManagementStore.loadingStatus === LoadingStatus.Loading}
      lockDeleteInDefaultProfile
    />
  );
});
